import { getProductAvailabilityEShopTwClass } from 'components/Blocks/Product/Availability/ProductAvailableStoresCount';
import { B2bProductPrice } from 'components/Blocks/Product/Price/B2bProductPrice';
import ProductPrice from 'components/Blocks/Product/Price/ProductPrice';
import { AddToCartButton } from 'components/Pages/ProductDetail/VirtualSet/AddToCartButton';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isB2cDomain } from 'helpers/isDomainLocale';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { useVirtualSetCalculatedAvailability } from 'hooks/virtualSet/useVirtualSetCalculatedAvailability';
import { useVirtualSetCalculatedPrices } from 'hooks/virtualSet/useVirtualSetCalculatedPrices';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { VirtualSetProductType } from 'types/product';
import twMerge from 'utils/twMerge';

type VirtualSetSummaryProps = {
    includedProducts: VirtualSetProductType[];
};

export const VirtualSetSummary: FC<VirtualSetSummaryProps> = ({ includedProducts }) => {
    const { t } = useTranslation();
    const domainConfig = useDomainConfig();
    const { formatPriceNumber } = useFormatPrice();
    const { isUserLoggedIn } = useCurrentUserData();

    const productPrices = useMemo(() => {
        return Array.from(includedProducts.values()).map((product) => ({
            price: product.price,
            prices: product.prices,
        }));
    }, [includedProducts]);
    const productAvailabilities = useMemo(
        () =>
            includedProducts.map((product) => ({
                availability: product.availability,
                defaultStoreAvailability: product.defaultStoreAvailability,
                deliveryDaysFromCentralStore: product.deliveryDaysFromCentralStore,
            })),
        [includedProducts],
    );

    const { finalPrice, hasActionPrice, hasTescomaPrice } = useVirtualSetCalculatedPrices(productPrices);
    const { centralStore: centralStoreAvailability, defaultStore: defaultStoreAvailability } =
        useVirtualSetCalculatedAvailability(productAvailabilities);

    const isAddToCartEnabled = includedProducts.every(
        (product) =>
            product.maximumOrderableQuantity >= (isB2bDomain(domainConfig) ? product.b2bMinimumProductOrder ?? 1 : 1),
    );

    if (includedProducts.length === 0) {
        return (
            <div className="flex h-48 w-full items-center justify-center rounded-md bg-borderLighter p-5">
                {t('Please select at least one product from the set')}
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-y-4 rounded-md bg-borderLighter p-5">
            <div className="flex flex-col gap-y-2">
                <div
                    className={twMerge(
                        'text-h6 font-bold leading-6 text-green hover:no-underline',
                        getProductAvailabilityEShopTwClass(centralStoreAvailability.isInStock),
                    )}
                >
                    {centralStoreAvailability.message}
                </div>
                {defaultStoreAvailability && defaultStoreAvailability.storeName ? (
                    <div className={getProductAvailabilityEShopTwClass(defaultStoreAvailability.isInStock)}>
                        {defaultStoreAvailability.message}
                    </div>
                ) : null}
            </div>
            <div>
                {isB2cDomain(domainConfig) && (
                    <div className="text-small">
                        {hasTescomaPrice && !isUserLoggedIn && finalPrice.prices.tescoma && (
                            <ProductPrice {...finalPrice.prices.tescoma} isTescomaPrice />
                        )}
                        {hasActionPrice && <ProductPrice {...finalPrice.prices.basic} isOldPrice />}
                        {hasActionPrice && (
                            <div className="mb-2 w-fit bg-red p-1 text-tiny leading-tight text-white">
                                {`${t('You will save')} `}
                                {formatPriceNumber(
                                    getSellingPriceByDomain(finalPrice.prices.basic, domainConfig) -
                                        getSellingPriceByDomain(finalPrice.price, domainConfig),
                                )}
                            </div>
                        )}
                        <ProductPrice {...finalPrice.price} isActionPrice={hasActionPrice} />
                    </div>
                )}
                {isB2bDomain(domainConfig) && (
                    <B2bProductPrice
                        b2bPaymentDiscount={0}
                        basePrice={finalPrice.prices.basic}
                        isUnitPrice={false}
                        sellingPrice={finalPrice.price}
                        variant="list"
                    />
                )}
            </div>
            {isAddToCartEnabled && <AddToCartButton includedProducts={includedProducts} />}
        </div>
    );
};
